import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { Icon } from '../../components/ui/Icon';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip, statusTone } from '../../components/ui/Chip';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Avatar } from '../../components/ui/Avatar';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate } from '../../lib/utils';
import './suppliers.css';

interface SupplierDetail {
  id: string;
  fullName: string;
  vat: string | null;
  taxCode: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  hourlyRate: string | null;
  dailyRate: string | null;
  active: boolean;
  notes: string | null;
  pec: string | null;
  addressLine: string | null;
  isPerson: boolean;
  /** Id dell'anagrafica su FattureInCloud: se c'è, la scheda è arrivata dal sync e non a mano. */
  ficSupplierId: number | null;
  skills: Array<{ skillId: string; level: number; skill: { name: string } }>;
  documents: Array<{ id: string; name: string; storageKey: string; uploadedAt: string }>;
  projectMembers: Array<{
    id: string; role: string; hoursPlanned: number;
    project: { id: string; code: string; name: string; status: string; division: string };
  }>;
  /** Lo stesso soggetto dal lato CRM, quando ci compra oltre che venderci. */
  alsoClient: { id: string; shortName: string; legalName: string } | null;
  /** Ultime 20 fatture ricevute; i totali di `payablesStats` sono su tutte. */
  payables: Array<{ id: string; number: string | null; issueDate: string; dueDate: string | null; total: string; status: string; description: string | null; category: string | null }>;
  payablesStats: { count: number; total: number; open: number };
}

type Tab = 'overview' | 'fatture' | 'progetti' | 'documenti';

export function SupplierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [tab, setTab] = useState<Tab>('overview');
  const qc = useQueryClient();

  const { data: s, isLoading, isError } = useQuery({
    queryKey: ['supplier', id],
    queryFn: () => api.get<SupplierDetail>(`/suppliers/${id}`),
    enabled: !!id,
    retry: false,
  });

  const toggleActive = useMutation({
    mutationFn: () => api.patch(`/suppliers/${id}`, { active: !s?.active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplier', id] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast.ok(s?.active ? 'Professionista disattivato' : 'Professionista attivato');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/suppliers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast.ok('Professionista eliminato');
      navigate('/suppliers');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  if (!id) return <Navigate to="/suppliers" replace />;
  if (isError) return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Professionisti', to: '/suppliers' }, { label: 'Professionista' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Professionista non trovato</h1>
        <div className="mute mt-2">L'ID richiesto non esiste.</div>
      </div>
    </div>
  );
  if (isLoading || !s) return <DetailSkeleton header="avatar" />;

  const activeProjects = s.projectMembers.filter((m) => m.project.status !== 'CHIUSO' && m.project.status !== 'IN_PAUSA');

  return (
    // m-detail: sotto i 768px la scheda diventa una colonna sola e la griglia
    // skill/contatti collassa (os-planning/mobile.md §7). Sul computer la
    // classe non dichiara niente.
    <div className="page m-detail">
      <Breadcrumb items={[{ label: 'Professionisti', to: '/suppliers' }, { label: s.fullName }]} />

      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora che si
            disegna; detail-meta e chip stato restano sotto come prima ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="flex gap-4 items-start max-w-[760px] min-w-0">
          <Avatar initials={s.fullName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()} size={56} />
          <div className="min-w-0">
            {/* L1 protagonista: il nome, tronca se lungo */}
            <h1 className="ed-title truncate">{s.fullName}</h1>
            {/* L3 meta-indice: una riga mute (P.IVA · email · città) */}
            <div className="detail-meta mt-2 truncate">
              <span>{s.isPerson ? 'Professionista' : 'Fornitore'}</span>
              {s.vat && <><span className="mute-2">·</span><span className="mono">{s.vat}</span></>}
              {s.email && <><span className="mute-2">·</span><span>{s.email}</span></>}
              {s.city && <><span className="mute-2">·</span><span>{s.city}</span></>}
            </div>
            {/* Stato: riga dedicata SOTTO; un solo chip colorato */}
            <div className="flex gap-1.5 mt-2.5 items-center">
              <Chip tone={s.active ? 'ok' : 'ghost'} dot>{s.active ? 'Attivo' : 'Inattivo'}</Chip>
              {/* Stesso soggetto, altra faccia: la scheda CRM resta la sua,
                  qui c'è solo il rimando. Niente fusione dei due record. */}
              {s.alsoClient && (
                <button type="button" className="chip-label" onClick={() => navigate(`/clients/${s.alsoClient!.id}`)} title={`Scheda cliente · ${s.alsoClient.legalName}`}>
                  anche cliente →
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 pb-1">
          {/* d-only sul solo bottone primario: sul telefono scende nella barra
              in fondo (mobile.md §4). Il menù "..." resta qui, dove deve stare
              la roba secondaria, e da lì è ancora raggiungibile. */}
          <button className="btn btn-primary d-only" onClick={() => openModal('uploadDoc', { scope: 'supplier', scopeId: s.id })}>
            <Icon name="upload" /> Documento
          </button>
          <DesktopOnly
            cosa="La modifica o eliminazione del professionista"
            perche="sono operazioni di anagrafica da computer; dal telefono puoi contattarlo e caricare un documento."
          >
            <ActionsMenu
              variant="ghost"
              items={[
              {
                key: 'toggleActive',
                label: s.active ? 'Disattiva' : 'Attiva',
                icon: s.active ? 'lock' : 'check',
                onClick: () => toggleActive.mutate(),
              },
              {
                key: 'delete',
                label: 'Elimina',
                icon: 'trash',
                danger: true,
                onClick: async () => {
                  const ok = await confirmDialog({
                    title: `Eliminare ${s.fullName}?`,
                    body: 'L\'anagrafica verrà rimossa definitivamente.',
                    danger: true,
                    confirmLabel: 'Elimina',
                  });
                  if (ok) del.mutate();
                },
              },
              ]}
            />
          </DesktopOnly>
        </div>
      </div>

      {/* Strip KPI: numeri NUDI sul foglio (stat-band), niente gabbia —
          una cella per concetto, entrambe le tariffe qui; gating canSeeMargin */}
      <div className="stat-band">
        {/* Quanto ci è costato: è il primo numero che serve su una scheda
            fornitore, e prima non c'era perché le fatture ricevute non erano
            agganciate a nessuno. */}
        <div className="st-click" role="button" tabIndex={0} onClick={() => setTab('fatture')} onKeyDown={(e) => { if (e.key === 'Enter') setTab('fatture'); }}>
          <div className="st-label">Speso con lui</div>
          <div className="st-value"><span className="u">€ </span><AnimatedNumber value={s.payablesStats.total} format={fmtEur} /></div>
          <div className="st-delta">{s.payablesStats.count} fatture ricevute</div>
        </div>
        <div>
          <div className="st-label">Da pagare</div>
          <div className={`st-value ${s.payablesStats.open > 0 ? 'text-alert' : ''}`}><span className="u">€ </span><AnimatedNumber value={s.payablesStats.open} format={fmtEur} /></div>
          {/* "€ 0 ancora aperto" non dice niente: a zero si scrive che non c'è
              niente in sospeso, come fa la scheda contatto. */}
          <div className="st-delta">{s.payablesStats.open > 0 ? 'ancora aperto' : 'niente da pagare'}</div>
        </div>
        <div>
          <div className="st-label">Progetti attivi</div>
          <div className="st-value"><AnimatedNumber value={activeProjects.length} /></div>
          {/* Stessa ragione: "su 0 totali" è un conto che non esiste. */}
          <div className="st-delta">{s.projectMembers.length > 0 ? `su ${s.projectMembers.length} totali` : 'nessuno collegato'}</div>
        </div>
        {showMargin ? (
          <div>
            <div className="st-label">Tariffa oraria</div>
            {/* Tariffa non impostata: si dice, non si scrive "€ 0" — che
                sarebbe un dato, e sbagliato. */}
            {Number(s.hourlyRate ?? 0) > 0
              ? <div className="st-value"><span className="u">€</span><AnimatedNumber value={Number(s.hourlyRate ?? 0)} format={fmtEur} /></div>
              : <div className="st-value mute-2">—</div>}
            <div className="st-delta">{Number(s.dailyRate ?? 0) > 0 ? `€ ${fmtEur(s.dailyRate ?? 0)} al giorno` : 'non impostata'}</div>
          </div>
        ) : (
          <div>
            <div className="st-label">Tariffa oraria</div>
            <div className="st-value mute-2">•••</div>
            <div className="st-delta">Riservato</div>
          </div>
        )}
      </div>

      <div className="page-tabs">
        {(['overview', 'fatture', 'progetti', 'documenti'] as Tab[]).map((t) => (
          <div key={t} className="page-tab" aria-selected={tab === t} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'fatture' && s.payablesStats.count > 0 && <span className="c">{s.payablesStats.count}</span>}
          </div>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
          {/* Skill nude sul foglio: kicker + conteggio, niente wrapper-card */}
          <div className="min-w-0">
            <div className="ed-section">
              <div className="data-hd">
                <span className="t-micro">Skill</span>
                {s.skills.length > 0 && <span className="mute text-[12px] mono">{s.skills.length} registrate</span>}
              </div>
              {s.skills.length ? (
                <div className="flex flex-wrap gap-2">
                  {/* Livello a pallini viola (rating = accento, mai semaforo) */}
                  {s.skills.map((sk) => (
                    <div key={sk.skillId} className="flex items-center gap-1.5 px-2.5 py-1 rounded-[var(--r-ctl)] border border-line bg-paper-2">
                      <span className="text-[13px]">{sk.skill.name}</span>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map((l) => (
                          <span key={l} className="w-1.5 h-1.5 rounded-full"
                            style={{ background: l <= sk.level ? 'var(--accent)' : 'var(--line-2)' }} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon="brief" title="Nessuna skill" description="Le competenze registrate aiutano a trovare il professionista giusto per i progetti." />
              )}
            </div>
          </div>

          {/* Colonna laterale: kicker-blocks senza bordi, impilati con hairline */}
          <div className="min-w-0">
            <div className="data-hd"><span className="t-micro">Contatti</span></div>
            <div className="ed-kv">
              <div className="ed-kv-row"><div className="k">Email</div><div className="v">{s.email ?? '—'}</div></div>
              <div className="ed-kv-row"><div className="k">Telefono</div><div className="v">{s.phone ?? '—'}</div></div>
              <div className="ed-kv-row"><div className="k">PEC</div><div className="v">{s.pec ?? '—'}</div></div>
              {/* Un indirizzo si scrive con le virgole. Le tre schede dello
                  stesso soggetto (contatto, cliente, professionista) lo
                  mostravano in tre modi diversi: qui e altrove ora è uno. */}
              <div className="ed-kv-row"><div className="k">Sede</div><div className="v">{[s.addressLine, s.city].filter(Boolean).join(', ') || '—'}</div></div>
              <div className="ed-kv-row"><div className="k">P.IVA</div><div className="v mono">{s.vat ?? '—'}</div></div>
              <div className="ed-kv-row"><div className="k">C.F.</div><div className="v mono">{s.taxCode ?? '—'}</div></div>
            </div>
            {/* Dove è nata la scheda: se viene da FIC i campi vuoti sono vuoti
                anche lì, e riempirli qui a mano è l'unico modo. */}
            {s.ficSupplierId != null && (
              <div className="mute t-small mt-2">Anagrafica sincronizzata da FattureInCloud</div>
            )}

            <div className="ed-divider" />

            <div className="data-hd"><span className="t-micro">Note interne</span></div>
            {s.notes ? (
              <div className="whitespace-pre-wrap">{s.notes}</div>
            ) : (
              <div className="mute t-small">Nessuna nota</div>
            )}
          </div>
        </div>
      )}

      {tab === 'fatture' && (
        <div className="ed-section">
          <div className="data-hd">
            <div className="flex items-baseline gap-2">
              <span className="t-micro">Fatture ricevute</span>
              <span className="mute text-[12px] mono">{s.payablesStats.count}</span>
            </div>
            {/* Come sulla scheda cliente: la tabella è un estratto, i totali no. */}
            {s.payablesStats.count > s.payables.length && (
              <span className="mute text-[12px]">ultime {s.payables.length} · i totali sono su tutte</span>
            )}
          </div>
          {/* tbl--card: sotto i 768px ogni riga diventa una scheda. */}
          <table className="tbl tbl--ed tbl--card">
            <thead><tr><th>Numero</th><th>Emissione</th><th>Scadenza</th><th>Causale</th><th>Stato</th><th className="num">Totale</th></tr></thead>
            <tbody>
              {/* m-static: la riga non si apre, quindi in scheda non porta il
                  chevron che vuol dire "si apre". */}
              {s.payables.map((p) => (
                <tr key={p.id} className="m-static">
                  <td data-m="hd" className="mono">{p.number ?? 's/n'}</td>
                  <td data-m="key" data-l="Emissione" className="mono mute">{fmtDate(p.issueDate)}</td>
                  <td data-m="key" data-l="Scadenza" className="mono mute">{fmtDate(p.dueDate)}</td>
                  <td data-m="row" data-l="Causale" className="mute truncate sup-cell-causale" style={{ maxWidth: 280 }}>{p.description ?? p.category ?? '—'}</td>
                  <td data-m="key" data-l="Stato"><Chip tone={statusTone(p.status)} dot>{p.status.replace('_', ' ')}</Chip></td>
                  <td data-m="key" data-l="Totale" className="num tbl-metric">€ {fmtEur(p.total)}</td>
                </tr>
              ))}
              {!s.payables.length && (
                <tr>
                  <td colSpan={6} style={{ padding: 0 }}>
                    <EmptyState icon="invoice" title="Nessuna fattura ricevuta" description="Le fatture ricevute arrivano da FattureInCloud e si agganciano da sole a questa scheda." />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'progetti' && (
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Progetti collegati</span>
            <span className="mute text-[12px] mono">{s.projectMembers.length}</span>
          </div>
          {/* Niente colonna-codice (resta nel dettaglio progetto); divisione come
              seconda riga del nome invece di una colonna dedicata */}
          <table className="tbl tbl--ed tbl--card">
            <thead><tr><th>Progetto</th><th>Ruolo</th><th className="num">Ore pianif.</th><th>Stato</th></tr></thead>
            <tbody>
              {s.projectMembers.map((m) => (
                <tr key={m.id} onClick={() => navigate(`/projects/${m.project.id}`)}>
                  <td data-m="hd" className="cell-2l">
                    <div className="p">{m.project.name}</div>
                    <div className="s">{m.project.division}</div>
                  </td>
                  <td data-m="key" data-l="Ruolo" className="mute">{m.role}</td>
                  <td data-m="key" data-l="Ore pianificate" className="num mono mute">{m.hoursPlanned}h</td>
                  <td data-m="key" data-l="Stato"><Chip tone={statusTone(m.project.status)} dot>{m.project.status.replace('_', ' ')}</Chip></td>
                </tr>
              ))}
              {!s.projectMembers.length && (
                <tr>
                  <td colSpan={4} style={{ padding: 0 }}>
                    <EmptyState icon="cube" title="Nessun progetto collegato" description="Assegna il professionista a un progetto e lo vedrai comparire qui." />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'documenti' && (
        <div className="ed-section">
          {/* Una sola CTA di upload per vista: quella primaria nell'header pagina */}
          <div className="data-hd">
            <span className="t-micro">Documenti</span>
            <span className="mute text-[12px] mono">{s.documents.length}</span>
          </div>
          <table className="tbl tbl--ed tbl--card">
            <thead><tr><th>Nome</th><th>Data</th></tr></thead>
            <tbody>
              {/* m-static: un documento non ha una scheda sua. */}
              {s.documents.map((d) => (
                <tr key={d.id} className="m-static">
                  <td data-m="hd" className="font-medium">{d.name}</td>
                  <td data-m="key" data-l="Data" className="mono mute">{fmtDate(d.uploadedAt)}</td>
                </tr>
              ))}
              {!s.documents.length && (
                <tr>
                  <td colSpan={2} style={{ padding: 0 }}>
                    <EmptyState icon="file" title="Nessun documento" description='Carica contratti e accordi con il pulsante "Documento" in alto.' />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Telefono: l'azione principale della scheda, dove arriva il pollice. */}
      <div className="m-actionbar m-only">
        <button className="btn btn-primary" onClick={() => openModal('uploadDoc', { scope: 'supplier', scopeId: s.id })}>
          <Icon name="upload" /> Documento
        </button>
      </div>
    </div>
  );
}
